import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { InputBase } from '@mui/material';
import type { OnSubmitCommentReturn } from 'src/components';
import { AlineAvatar } from 'src/components/AlineAvatar';
import { useSelectCurrentUser } from 'src/gql/hooks/user';
import { getOnEnterHandler } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  avatar: {
    flexShrink: 0,
  },
  input: {
    color: theme.appColors.iconsType,
    fontSize: 14,
  },
  inputRoot: {
    flex: 1,
    gap: theme.spacing(1),
  },
  root: {
    alignItems: 'center',
    display: 'flex',
  },
}));

interface ActivityReplyComposerProps {
  /** The thread's top-level comment. A reply to a channel thread is sent back to that channel. */
  commentId: UUID;
  onSubmitReply: (parentCommentId: UUID, markdown: string) => Promise<OnSubmitCommentReturn>;
  placeholder?: string;
}

/** A one-line reply box under a thread. Enter sends; the draft stays if sending fails. */
export const ActivityReplyComposer = React.memo(function ActivityReplyComposer({
  commentId,
  onSubmitReply,
  placeholder = 'Leave a reply...',
}: ActivityReplyComposerProps) {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!currentUser) return null;

  async function handleSubmit() {
    const trimmed = replyText.trim();
    if (!trimmed || submitting) return;

    setSubmitting(true);
    try {
      const result = await onSubmitReply(commentId, trimmed);
      if (result) setReplyText('');
    } catch {
      // Keep the draft; the global Apollo errorLink surfaces the failure.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={classes.root}>
      <InputBase
        className={classes.inputRoot}
        classes={{ input: classes.input }}
        disabled={submitting}
        placeholder={placeholder}
        startAdornment={
          <AlineAvatar
            avatarInfo={currentUser}
            className={classes.avatar}
            styles={{ size: 'sm' }}
          />
        }
        value={replyText}
        onChange={event => setReplyText(event.target.value)}
        onKeyDown={getOnEnterHandler(handleSubmit)}
      />
    </div>
  );
});
